import { ChangeDetectionStrategy, Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, defer, iif, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  Owner,
  OwnersSearchFullGQL,
  OwnersSearchFullQuery,
} from '../../../../../../graphql/service/graphql-shared-familiar-service';

@Component({
  selector: 'app-lot-add-car-inspection-admin-form-modal-agent',
  templateUrl: './lot-add-car-inspection-admin-form-modal-agent.component.html',
  styleUrls: ['./lot-add-car-inspection-admin-form-modal-agent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => LotAddCarInspectionAdminFormModalAgentComponent),
    },
  ],
})
export class LotAddCarInspectionAdminFormModalAgentComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  owners$: Observable<OwnersSearchFullQuery['owners']['nodes']>;
  searching$ = new BehaviorSubject<boolean>(false);
  loading$ = of(false);

  onChange?: (value: number) => void;
  onTouched?: () => unknown;

  ownerId = null;
  ownerName$ = new BehaviorSubject<string>('');

  disabled = false;

  private destroy$ = new Subject<void>();

  constructor(private ownersSearchFullGQL: OwnersSearchFullGQL) {}

  ngOnInit(): void {
    this.initFetchOwners();
  }

  initFetchOwners(): void {
    const defaultOwners$ = this.searchOwnersOwners('AKEBONO скраб').pipe(shareReplay(1));

    this.loading$ = defaultOwners$.pipe(
      map(() => false),
      startWith(true),
      shareReplay(1),
    );

    const ownerNameDebounced$ = this.ownerName$.pipe(
      debounceTime(500),
      map((name) => name.trim()),
      shareReplay(1),
    );

    const ownersByName$ = ownerNameDebounced$.pipe(
      switchMap((name) =>
        iif(
          () => name.length >= 3,
          defer(() => {
            this.searching$.next(true);
            return this.searchOwnersOwners(name).pipe(
              tap(() => this.searching$.next(false)),
              startWith([]),
            );
          }),
          of([]),
        ),
      ),
      startWith([]),
    );

    const emitDefaultOwners$ = ownerNameDebounced$.pipe(
      switchMap((ownerName) => (ownerName.length ? of(null) : defaultOwners$)),
      shareReplay(1),
    );

    this.owners$ = combineLatest([ownersByName$, emitDefaultOwners$]).pipe(
      map(([ownersByName, defaultOwners]) => defaultOwners ?? ownersByName),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
      shareReplay(1),
    );
  }

  private searchOwnersOwners(
    ownerNameSearch: string,
  ): Observable<OwnersSearchFullQuery['owners']['nodes']> {
    return this.ownersSearchFullGQL
      .fetch(
        {
          search: ownerNameSearch,
          filter: {
            owner: {
              hasYahooAccount: true,
            },
          },
        },
        { fetchPolicy: 'network-only' },
      )
      .pipe(map((response) => response?.data.owners?.nodes ?? []));
  }

  writeValue(): void {}

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => unknown): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  selectTouch(): void {
    this.onTouched?.();
  }

  selectChange(ownerId: number | null): void {
    console.log('gg', ownerId);
    this.onChange?.(ownerId);
  }

  getLabel(owner: Owner): string {
    if (owner.uuid) {
      return `(${owner.uuid}) ${owner.name} `;
    }

    return owner.name;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
